"use client";

import { useQuery } from "@apollo/client/react";
import Link from "next/link";
import { memo, useContext } from "react";
import { LuCrown, LuUserMinus, LuUserPlus } from "react-icons/lu";

import { setDialog } from "@/components/app/dialogs/dialog-provider";
import { FollowUserListsButton } from "@/components/app/lists/follow-user-lists-button";
import { PostUpvote } from "@/components/app/post/post-upvote";
import { SubscribeButton } from "@/components/app/subscribe-button";
import { AccountSocialCard } from "@/components/shared/account-social-card";
import CoinLivePrice from "@/components/shared/coin-live-price";
import { DesktopTooltip } from "@/components/shared/desktop-tooltip";
import DesoAvatar from "@/components/shared/deso-avatar";
import { TriggerLoggedInModalWindow } from "@/components/shared/is-logged-in";
import { Button } from "@/components/ui/button";
import { DropdownDrawerItem } from "@/components/ui/dropdown-drawer";
import type { PostType } from "@/contexts/feed-context";
import { usePostContext } from "@/contexts/post-context";
import { UserContext } from "@/contexts/user-context";
import { SubscriptionByCreatorAndSubscriberDocument } from "@/graphql/codegen/graphql";
import { useUserAssociatedLists } from "@/hooks/use-user-associated-lists";
import { useUserFollowStatus } from "@/hooks-api/use-user-follow-status";
import { DIALOG } from "@/utils/dialog-routes";
import { cn } from "@/utils/lib";
import { SubscriptionStatus } from "@/utils/subscriptions";
import { getUserName } from "@/utils/usernames";

import { TimestampTooltip } from "../../shared/timestamp-tooltip";
import { UserDisplayNameWithStatus } from "../../shared/user-display-name-with-status";
import { PostActionsDropdown } from "./post-actions/post-actions-dropdown";

export enum PostDetailsView {
  innerPost = "innerPost",
  feed = "feed",
  details = "details",
}

interface DetailTypeAuthorProps {
  poster: PostType["poster"];
  postType?: PostDetailsView;
  timestamp: string;
}

export const PostDetailsHeader = memo(
  ({
    postType = PostDetailsView.details,
    timestamp,
  }: DetailTypeAuthorProps) => {
    const { user, isUserBlocked, currentUserPublicKey } =
      useContext(UserContext);
    const {
      poster,
      hideHeaderActions,
      hideUpvote,
      post,
      repost,
      isPureRepost,
    } = usePostContext();
    const postHash = isPureRepost ? repost?.postHash : post?.postHash;
    const posterPublicKey = poster?.publicKey ?? "";
    const hasSubscriptionTiers =
      (poster?.subscriptionTiers?.totalCount ?? 0) > 0;
    const isCurrentUserPost = user?.publicKey === posterPublicKey;
    const { isUserFollowedByPublicKey } = useUserFollowStatus(posterPublicKey);
    const { data: associatedLists = [] } = useUserAssociatedLists({
      ownerPublicKey: currentUserPublicKey ?? "",
      userPublicKey: posterPublicKey,
      enabled:
        !!currentUserPublicKey &&
        !!posterPublicKey &&
        currentUserPublicKey !== posterPublicKey,
    });
    const { data: subscriptionData } = useQuery(
      SubscriptionByCreatorAndSubscriberDocument,
      {
        variables: {
          viewerPublicKey: currentUserPublicKey,
          subscriberPublicKey: currentUserPublicKey,
          creatorPublicKey: posterPublicKey,
        },
        fetchPolicy: "cache-and-network",
        skip:
          !currentUserPublicKey ||
          !posterPublicKey ||
          !hasSubscriptionTiers ||
          isCurrentUserPost,
      },
    );

    if (!poster) return null;

    if (!posterPublicKey) {
      throw new Error("Poster is required");
    }

    const isBlocked = isUserBlocked(posterPublicKey);
    if (isBlocked) {
      return null;
    }

    const publicKey = posterPublicKey;
    const posterHandle = getUserName({
      account: poster,
      truncateLength: 40,
    });
    const isFollowActionActive =
      isUserFollowedByPublicKey || associatedLists.length > 0;
    const isSubscribed =
      (subscriptionData?.focusSubscriptions?.nodes?.length ?? 0) > 0 &&
      !!subscriptionData?.focusSubscriptions?.nodes?.find(
        (subscription) =>
          subscription?.extraData?.Status === SubscriptionStatus.Active,
      );
    const FollowActionIcon = isFollowActionActive ? LuUserMinus : LuUserPlus;
    const openFollowLists = () => {
      setDialog(DIALOG.FollowUserLists, {
        open: true,
        replace: true,
        params: {
          userLabel: posterHandle,
          userPublicKey: publicKey,
        },
      });
    };

    const renderActions = () => {
      return (
        <div className="flex items-center gap-2">
          {!isCurrentUserPost && (
            <div
              className="
                hidden items-center gap-2
                sm:flex
              "
            >
              <FollowUserListsButton
                userLabel={posterHandle}
                userPublicKey={publicKey}
              />

              {hasSubscriptionTiers && (
                <SubscribeButton
                  creator={poster}
                  renderContext="profile"
                  renderTrigger={({ isSubscribed, loading }) => {
                    if (isSubscribed) {
                      return (
                        <DesktopTooltip content="Unsubscribe">
                          <Button
                            variant="default"
                            className="
                              hidden size-10 shrink-0 rounded-full px-0
                              sm:inline-flex
                            "
                            loading={loading}
                          >
                            <LuCrown className="size-4 shrink-0" />
                            <span className="sr-only">Unsubscribe</span>
                          </Button>
                        </DesktopTooltip>
                      );
                    }

                    return (
                      <div className="contents">
                        <Button
                          variant="outlinePrimary"
                          className="
                            hidden h-10 shrink-0 gap-2 rounded-full px-3
                            @min-[560px]:inline-flex
                          "
                          loading={loading}
                        >
                          <LuCrown className="size-4 shrink-0" />
                          <span>Subscribe</span>
                        </Button>
                        <DesktopTooltip content="Subscribe">
                          <Button
                            variant="outlinePrimary"
                            className="
                              inline-flex size-10 shrink-0 rounded-full px-0
                              @min-[560px]:hidden
                            "
                            loading={loading}
                          >
                            <LuCrown className="size-4 shrink-0" />
                            <span className="sr-only">Subscribe</span>
                          </Button>
                        </DesktopTooltip>
                      </div>
                    );
                  }}
                >
                  Subscribe
                </SubscribeButton>
              )}
            </div>
          )}
          <div
            className="
              absolute top-[14px] right-[40px]
              sm:relative sm:top-0 sm:right-0
            "
          >
            <PostActionsDropdown compact account={poster}>
              {!isCurrentUserPost && (
                <>
                  <DropdownDrawerItem
                    className="sm:hidden"
                    onSelect={openFollowLists}
                  >
                    <FollowActionIcon className="mr-2 size-4 shrink-0 text-muted" />
                    <span>{isFollowActionActive ? "Unfollow" : "Follow"}</span>
                  </DropdownDrawerItem>
                  {hasSubscriptionTiers && (
                    <SubscribeButton
                      creator={poster}
                      dialogReplace
                      renderContext="post"
                      renderTrigger={({ openDialog }) => (
                        <DropdownDrawerItem
                          className="sm:hidden"
                          onSelect={openDialog}
                        >
                          <LuCrown className="mr-2 size-4 shrink-0 text-muted" />
                          <span>
                            {isSubscribed ? "Unsubscribe" : "Subscribe"}
                          </span>
                        </DropdownDrawerItem>
                      )}
                    />
                  )}
                </>
              )}
            </PostActionsDropdown>
          </div>
        </div>
      );
    };

    return (
      <div
        className="
        @container flex w-full flex-row items-start justify-between
      "
      >
        <div
          className="
            flex max-w-[calc(100%-80px)] grow flex-row items-start gap-3
            overflow-hidden whitespace-nowrap
            md:items-center
          "
          style={{
            WebkitMaskImage:
              "linear-gradient(90deg, #000000FF 90%, #00000000 100%)",
          }}
        >
          <div
            className={cn("block", {
              "md:hidden": postType === PostDetailsView.feed,
            })}
          >
            {poster && (
              <AccountSocialCard
                publicKey={publicKey}
                username={poster.username}
                clickable
              >
                <div className="inline-flex cursor-pointer">
                  <DesoAvatar account={poster} size={60} />
                </div>
              </AccountSocialCard>
            )}
          </div>
          <div className="flex flex-col md:gap-1">
            <div
              className="
                flex flex-wrap items-center gap-2 font-sans!
                md:flex-nowrap
              "
            >
              <UserDisplayNameWithStatus whaleSize={24} account={poster} />
            </div>

            <div className="flex flex-wrap items-center">
              <div className="mt-[-2px] flex items-center gap-1 md:mt-0">
                <div
                  className="
                    ml-0 inline-block max-w-[100px] truncate text-sm text-muted
                    sm:max-w-none
                  "
                >
                  {getUserName({ account: poster, truncateLength: 15 })}
                </div>
                <span className="inline-block text-xs font-light text-muted">
                  &bull;
                </span>
                <Link href={`/post/${postHash}`}>
                  <TimestampTooltip timestamp={timestamp} className="block" />
                </Link>
              </div>
              {poster?.creatorBasisPoints && !!poster.publicKey && (
                <TriggerLoggedInModalWindow className="flex">
                  <CoinLivePrice publicKey={poster.publicKey} />
                </TriggerLoggedInModalWindow>
              )}
            </div>
          </div>
        </div>

        <div className="relative flex items-center">
          {!hideHeaderActions && (
            <TriggerLoggedInModalWindow
              className="
              -mr-2 flex items-center gap-2
            "
            >
              {renderActions()}
            </TriggerLoggedInModalWindow>
          )}
          {!hideUpvote && (
            <div
              className={cn({
                "md:hidden": postType === PostDetailsView.feed,
                hidden: postType === PostDetailsView.innerPost,
              })}
            >
              <div className="ml-2">
                <PostUpvote />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  },
);

PostDetailsHeader.displayName = "PostDetailsHeader";
